import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Modal, Form, Row, Col } from "react-bootstrap";
import { setBillingInfo as setBillingInfoState } from "state/orderEdition/actions";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCopy, faPen } from '@fortawesome/free-solid-svg-icons'
import Select from "react-select";
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import AddressAutocompleteInput from "./AddressAutocompleteInput";

const BillingInfoComponent = ({ title, showCompany, dispatchAddress }) => {
  const dispatch = useDispatch();
  const deliveryInfoState = useSelector(
    (state) => state.orderEdition.deliveryInfo
  );
  const billingInfoState = useSelector(
    (state) => state.orderEdition.billingInfo
  );
  const addresses = useSelector((state) => state?.adminData?.addresses || []);
  const [addressesFormat, setAddressesFormat] = useState([]);
  const [optionSelected, setSelectedOptions] = useState(null);

  const formatPreview = useCallback((address) => {
    return [
      address?.street_number,
      address?.street,
      address?.postal_code,
      address?.city,
    ]
      .filter(Boolean)
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();
  }, []);

  const buildAddressOption = useCallback(
    (address) => {
      const preview = formatPreview(address);
      return {
        value: address.id,
        label: [address.name_address, preview].filter(Boolean).join(" - "),
        preview,
        ...address,
      };
    },
    [formatPreview]
  );

  const filterAddressOption = (option, rawInput) => {
    if (!rawInput) {
      return true;
    }
    const input = rawInput.toLowerCase();
    const data = option.data || option;
    return [
      option.label,
      data.name_address,
      data.preview,
      data.street,
      data.street_number,
      data.postal_code,
      data.city,
    ]
      .filter(Boolean)
      .some((value) => value.toString().toLowerCase().includes(input));
  };

  const formatAddressOptionLabel = (option) => (
    <div>
      <div>{option.name_address || option.label}</div>
      <small className="text-muted">
        {option.preview || formatPreview(option) || "Adresse inconnue"}
      </small>
    </div>
  );

  useEffect(() => {
    const formatted = addresses.map(buildAddressOption);
    setAddressesFormat(formatted);
    const selectedId = billingInfoState?.addressBook?.id;
    setSelectedOptions(
      formatted.find((item) => item.value === selectedId) || null
    );
  }, [addresses, billingInfoState?.addressBook?.id, buildAddressOption]);

  const [show, setShow] = useState(false);
  const [billingInfo, setBillingInfo] = useState({
    lastname: billingInfoState?.lastname || "",
    firstname: billingInfoState?.firstname || "",
    phone: billingInfoState?.phone || "",
    streetNumber: billingInfoState?.streetNumber || "",
    street: billingInfoState?.street || "",
    postalCode: billingInfoState?.postalCode || "",
    city: billingInfoState?.city || "",
    addressComplement: billingInfoState?.addressComplement || "",
    company: billingInfoState?.company || "",
    service: billingInfoState?.service || "",
  });

  useEffect(() => {
    if (billingInfoState) {
      setBillingInfo(billingInfoState);
    }
  }, [billingInfoState]);

  const copyDeliveryInfo = () => {
    const fullAddress = deliveryInfoState?.streetNumber + " " + deliveryInfoState?.street + " " + deliveryInfoState?.postalCode + " " + deliveryInfoState?.city;
    setBillingInfo({
      ...billingInfo,
      phone: deliveryInfoState?.phone|| "",
      addressComplement: deliveryInfoState?.addressComplement || "",
      fullAddress: fullAddress,
      company: deliveryInfoState?.company || "",
      streetNumber: deliveryInfoState?.streetNumber || "",
      street: deliveryInfoState?.street || "",
      postalCode: deliveryInfoState?.postalCode || "",
      city: deliveryInfoState?.city || "",
      service: deliveryInfoState?.service || "",
    });
    dispatch(
      setBillingInfoState({
        ...billingInfo,
        phone: deliveryInfoState?.phone|| "",
        addressComplement: deliveryInfoState?.addressComplement || "",
        fullAddress: fullAddress,
        company: deliveryInfoState?.company || "",
        streetNumber: deliveryInfoState?.streetNumber || "",
        street: deliveryInfoState?.street || "",
        postalCode: deliveryInfoState?.postalCode || "",
        city: deliveryInfoState?.city || "",
        service: deliveryInfoState?.service || "",
      })
    );
  };

  const handleSelectAddressChange = (event) => {
    setSelectedOptions(event);
    const selectedAddress = addresses.find(
      (adresse) => adresse.id === Number(event.value)
    );
    if (selectedAddress) {
      const fullAddress = selectedAddress?.street_number + " " + selectedAddress?.street + " " + selectedAddress?.postal_code + " " + selectedAddress?.city;
      setBillingInfo({
        ...billingInfo,
        phone: selectedAddress?.phone|| "",
        addressComplement: selectedAddress?.address_complement || "",
        fullAddress: fullAddress,
        company: selectedAddress?.company || "",
        streetNumber: selectedAddress?.street_number || "",
        street: selectedAddress?.street || "",
        postalCode: selectedAddress?.postal_code || "",
        city: selectedAddress?.city || "",
        addressComplement: selectedAddress?.address_complement || "",
        service: selectedAddress?.service || "",
      });
    }
  };

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleChange = (e) => {
    setBillingInfo({
      ...billingInfo,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    if (e){
      e.preventDefault();
    }
    dispatch(
      setBillingInfoState({
        ...billingInfoState,
        ...billingInfo,
      })
    );
    handleClose();
  };

  return (
    <>
      <Modal show={show} onHide={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Informations de facturation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group>
              <Form.Label>Carnet d'adresses</Form.Label>
              <Select
                options={addressesFormat}
                closeMenuOnSelect={true}
                onChange={handleSelectAddressChange}
                value={optionSelected}
                name={"address"}
                formatOptionLabel={formatAddressOptionLabel}
                filterOption={filterAddressOption}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Nom</Form.Label>
              <Form.Control
                type="text"
                name="lastname"
                value={billingInfo.lastname}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Prénom</Form.Label>
              <Form.Control
                type="text"
                name="firstname"
                value={billingInfo.firstname}
                onChange={handleChange}
              />
            </Form.Group>
            {showCompany && <Form.Group>
              <Form.Label>Entreprise</Form.Label>
              <Form.Control
                type="text"
                name="company"
                value={billingInfo.company}
                onChange={handleChange}
              />
            </Form.Group>}
            <Form.Group>
              <Form.Label>Service du commanditaire</Form.Label>
              <Form.Control
                type="text"
                name="service"
                value={billingInfo.service}
                onChange={handleChange}
                placeholder="Service du commanditaire (facultatif)"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Téléphone</Form.Label>
              <Form.Control
                type="text"
                name="phone"
                value={billingInfo.phone}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Recherche d'adresse (remplit les champs ci-dessous)</Form.Label>
              <AddressAutocompleteInput
                onSelect={(address) =>
                  setBillingInfo({
                    ...billingInfo,
                    streetNumber: address.streetNumber,
                    street: address.street,
                    postalCode: address.postalCode,
                    city: address.city,
                    fullAddress: address.label,
                  })
                }
              />
            </Form.Group>
            {dispatchAddress ?
            <>
              <Form.Group>
                <Form.Label>Numéro de rue</Form.Label>
                <Form.Control
                  type="text"
                  name="streetNumber"
                  value={billingInfo.streetNumber}
                  onChange={handleChange}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Rue</Form.Label>
                <Form.Control
                  type="text"
                  name="street"
                  value={billingInfo.street}
                  onChange={handleChange}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Code Postal</Form.Label>
                <Form.Control
                  type="text"
                  name="postalCode"
                  value={billingInfo.postalCode}
                  onChange={handleChange}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Ville</Form.Label>
                <Form.Control
                  type="text"
                  name="city"
                  value={billingInfo.city}
                  onChange={handleChange}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Complément d'adresse</Form.Label>
                <Form.Control
                  type="text"
                  name="addressComplement"
                  value={billingInfo.addressComplement}
                  onChange={handleChange}
                />
              </Form.Group>
            </>
            :
            <>
              <Form.Group>
                <Form.Label>Adresse complète</Form.Label>
                <Form.Control
                  type="text"
                  name="fullAddress"
                  value={billingInfo.fullAddress}
                  onChange={handleChange}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Complément d'adresse</Form.Label>
                <Form.Control
                  type="text"
                  name="addressComplement"
                  value={billingInfo.addressComplement}
                  onChange={handleChange}
                />
              </Form.Group>
            </>
            }
            <Button variant="primary" type="submit">
              Enregistrer
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      <>
        <Row>
          <Col xs={6} sm={6} md={6}>
            <h5>{title}</h5>
          </Col>
          <Col xs={6} sm={6} md={6} className="text-right">
            <Tippy content="Copier info livraison sur facturation">
              <Button variant="success" size="sm" className="mr-2"  onClick={copyDeliveryInfo}>
                <FontAwesomeIcon icon={faCopy} />
              </Button>
            </Tippy>
            <Button variant="success" size="sm" onClick={handleShow}>
            <FontAwesomeIcon icon={faPen} />
            </Button>
          </Col>
        </Row>
        <p>
          {billingInfo.lastname || "Nom"} {billingInfo.firstname || "Prénom"}
          <br />
          {billingInfo.company || "Entreprise"}
          {billingInfo.service && (
            <>
              <br />
              Service: {billingInfo.service}
            </>
          )}
          <br />
          {billingInfo.phone || "Tel."}
          <br />
          {dispatchAddress ?
            <>
              {billingInfo.streetNumber || "N."}{" "}
              {billingInfo.street || "Rue"}
              <br />
              {billingInfo.postalCode || "Code postal"}{" "}
              {billingInfo.city || "Ville"}
            </>
          :
            <>
              {billingInfo.fullAddress || "Adresse"}
            </>
          }
          <br />
          {billingInfo.addressComplement || "Complém."}
        </p>
      </>
    </>
  );
};

export default BillingInfoComponent;
